import { EventEntityType } from '@atlas/types'
import { z } from 'zod'

/**
 * Body validators for the generic `comment-service` HTTP API
 * (`POST /comments`, `PUT /comments/:id`).
 *
 * `entityType` is the `EventEntityType` enum (the unified entity-kind
 * enum used by both events and comments) — keeps the comments primitive
 * decoupled from any one consumer. `metadata.mentions` is an optional
 * UUID-array of resolved employee ids surfaced by the frontend's
 * `@employee` autocomplete; one `COMMENT_MENTIONED` event fans out per
 * newly-added entry on create / edit (Spec 4 notification routing keys
 * on this).
 *
 * A comment needs text, a reference image or a link: content is trimmed and
 * may be empty only when `metadata` carries an attachment or a link.
 */

export const MAX_COMMENT_ATTACHMENTS = 10
export const MAX_COMMENT_LINKS = 10

/** Links are rendered as `href`s, so anything but an absolute http(s) URL is refused. */
const httpUrl = z
	.url({ protocol: /^https?$/, error: 'Link must be an http:// or https:// URL' })
	.max(2048, 'Link is too long (max 2,048 chars)')

export const commentMetadataSchema = z.looseObject({
	mentions: z.array(z.uuid()).optional(),
	attachments: z
		.array(z.object({ fileId: z.uuid() }))
		.max(MAX_COMMENT_ATTACHMENTS, `At most ${MAX_COMMENT_ATTACHMENTS} images per comment`)
		.transform((list) => list.filter((a, i) => list.findIndex((b) => b.fileId === a.fileId) === i))
		.optional(),
	links: z
		.array(
			z.object({
				url: httpUrl,
				// A blank label is no label: the link then shows its URL.
				label: z
					.string()
					.trim()
					.max(200)
					.optional()
					.transform((label) => label || undefined),
			}),
		)
		.max(MAX_COMMENT_LINKS, `At most ${MAX_COMMENT_LINKS} links per comment`)
		.optional(),
})

const commentContent = z.string().trim().max(10_000, 'Comment is too long (max 10,000 chars)')

const commentBodySchema = z.object({
	entityType: z.enum(EventEntityType),
	entityId: z.uuid(),
	content: commentContent.default(''),
	isInternal: z.boolean().optional(),
	metadata: commentMetadataSchema.optional(),
})

export const commentCreateSchema = commentBodySchema.superRefine((body, ctx) => {
	if (body.content || body.metadata?.attachments?.length || body.metadata?.links?.length) return
	ctx.addIssue({ code: 'custom', path: ['content'], message: 'Comment content is required' })
})

export type CommentCreateInput = z.infer<typeof commentCreateSchema>

/** Whether the merged comment still has a body is the service's check: only it sees the stored row. */
export const commentUpdateSchema = z
	.object({ content: commentContent, isInternal: z.boolean(), metadata: commentMetadataSchema })
	.partial()

export type CommentUpdateInput = z.infer<typeof commentUpdateSchema>
